import { Fragment, ReactNode } from "react";
import { bucatiMarcate, t, useT, type Cheie, type Valori } from "../i18n";
import HoverCard from "./HoverCard";
import { CorpInfo } from "./Info";
import { useCatalogMarimi, marimeDin, formulaCuValori, NATURA,
         EtichetaNaturaText, ValoriFormula } from "./Marime";

// ---------------------------------------------------------------------------
// Hover DIRECT PE SIMBOL — în text, în formule, în capetele de tabel
// ---------------------------------------------------------------------------
//
// Cerința: „oriunde sunt variabile sau constante cu
// litere să poți da hover și să îți spună ce reprezintă". Până acum explicația
// era ancorată de iconița ⓘ de lângă o cifră, deci acoperea numai mărimile
// care aveau un card al lor. Un text de tipul „c = 3 − 2,3·T₁/T_C, plafonat la
// √(T_C·q)/1,7" nu explica nimic despre T_C — cine nu știa ce e T_C nu avea
// unde să întrebe, fiindcă întrebarea nu avea un loc pe pagină.
//
// Mecanismul e același cartonaș (`HoverCard`) și același corp de explicație
// (`CorpInfo`) ca la `<Marime>`. Ce se schimbă e DECLANȘATORUL: simbolul
// însuși, subliniat punctat.
//
// Două feluri de folosire:
//
//   <S k="E_c_ef">E_c,ef</S>          — marcare EXPLICITĂ, când textul e scris
//                                       în componentă și se știe ce e simbol.
//                                       Merge și pe simboluri de o literă
//                                       („q", „c"), care nu se adnotează
//                                       automat.
//
//   <TextSimboluri text={r.formula} /> — adnotare AUTOMATĂ a unui text venit
//                                       de la server (formule, note,
//                                       explicații). Indexul `simbol -> cheie`
//                                       vine tot de la server
//                                       (`catalog_marimi.simboluri()`), ca să
//                                       nu rămână în urma catalogului.
//
// De ce indexul NU se construiește în interfață: ar fi o a doua listă de
// simboluri, care ar diverge de catalog la prima mărime nouă — exact greșeala
// pe care catalogul o repară.

/** Ancora: simbolul, cu explicația lui la hover.
 *
 *  `children` e OBLIGATORIU, și nu din pedanterie: textul vizibil aparține
 *  frazei, nu catalogului. Cât timp catalogul nu a sosit, componenta randează
 *  exact `children` — deci fraza se citește întreagă de la primul cadru, iar
 *  hoverul apare o clipă mai târziu. Cu `children` opțional, fallback-ul era
 *  CHEIA de catalog, și pe ecran scria „E_c_ef" în loc de „E_c,ef" (prins în
 * harnașul de randare). */
export function S({
  k,
  children,
  valori,
}: {
  /** cheia din catalogul de mărimi */
  k: string;
  /** cum se scrie simbolul în fraza asta */
  children: ReactNode;
  /** valorile proiectului pentru placeholderele din formulă */
  valori?: ValoriFormula;
}) {
  const t = useT();
  const m = marimeDin(useCatalogMarimi(), k);
  // fără catalog (încă nesosit, sau cheie greșită) rămâne textul simplu:
  // un simbol nemarcat e mai bun decât o ancoră care nu deschide nimic
  if (!m) return <>{children}</>;
  return (
    <HoverCard
      clasaAncora="sb-ancora"
      eticheta={t("struct.info.explicatie_pentru", { ce: m.eticheta })}
      continut={
        <CorpInfo
          titlu={m.eticheta}
          natura={
            m.natura ? (
              <span className="fx-tip-block">
                <span className="fx-tip-label">{t("struct.simbol.ce_fel")}</span>
                <span>
                  <EtichetaNaturaText natura={m.natura} />{" "}
                  <span className="cv-de-unde">
                    {NATURA[m.natura] ? t(NATURA[m.natura].titlu) : ""}
                  </span>
                </span>
              </span>
            ) : null
          }
          ce={m.ce}
          deCe={
            <>
              {m.de_ce}
              {m.formula && (
                <>
                  <br />
                  <br />
                  <strong>{t("struct.simbol.formula")}</strong>{" "}
                  <span className="cv-formula mr-formula-tip">
                    {formulaCuValori(m.formula, valori)}
                  </span>
                </>
              )}
              {m.cum_obtinut && (
                <>
                  <br />
                  <strong>{t("struct.simbol.cum_obtinut")}</strong> {m.cum_obtinut}
                </>
              )}
              {m.articol && (
                <>
                  <br />
                  <strong>{t("struct.simbol.articol")}</strong> {m.articol}
                </>
              )}
              {m.contexte && m.contexte.length > 1 && (
                <>
                  <br />
                  <br />
                  <strong>{t("struct.simbol.contexte")}</strong>
                  {m.contexte.map((c) => (
                    <span key={c.categorie} style={{ display: "block", marginTop: 2 }}>
                      <em>{c.eticheta}</em>: {c.nume}
                      <span className="cv-de-unde"> — {c.de_unde}</span>
                    </span>
                  ))}
                </>
              )}
            </>
          }
          parametri={m.parametri}
        />
      }
    >
      <span className="sb">{children}</span>
    </HoverCard>
  );
}

// ---------------------------------------------------------------------------
// Adnotarea automată a unui text
// ---------------------------------------------------------------------------

/** Simbolurile din index, cele mai LUNGI întâi.
 *
 *  Ordinea contează: fără ea, „T_C" ar fi tăiat de „T" (dacă ar exista) și
 *  „E_c,ef" de „E_c" — prefixul ar câștiga și ar trimite la alt cartonaș. */
function ordonate(simboluri: Record<string, string>) {
  return Object.keys(simboluri).sort((a, b) => b.length - a.length);
}

/** Poziția primei apariții a unui simbol în `text`, ca TOKEN întreg.
 *
 *  „ca token întreg" e regula care ține adnotarea onestă: `γ` nu are voie să
 *  se potrivească în „γ_apă", iar `E_c` nu are voie în „E_cm". Vecinătatea se
 *  verifică pe caractere care pot face parte dintr-un simbol — litere, cifre,
 *  `_`, virgulă între indici, indici tipografici. */
function estePartedinSimbol(ch: string | undefined) {
  if (!ch) return false;
  if (/[A-Za-zĂÂÎȘȚăâîșț0-9_]/.test(ch)) return true;
  const c = ch.codePointAt(0) ?? 0;
  // indici tipografici (₀–₉), litere grecești, semne combinate
  return (c >= 0x2080 && c <= 0x209c) || (c >= 0x370 && c <= 0x3ff)
    || (c >= 0x300 && c <= 0x36f);
}

/** Textul spart în bucăți: șiruri simple + simboluri recunoscute. */
export function bucatiCuSimboluri(
  text: string,
  simboluri: Record<string, string>
): { text: string; cheie?: string }[] {
  const chei = ordonate(simboluri);
  const out: { text: string; cheie?: string }[] = [];
  let i = 0;
  let simplu = "";
  while (i < text.length) {
    let gasit: string | null = null;
    for (const s of chei) {
      if (!text.startsWith(s, i)) continue;
      // vecinii nu au voie să fie parte de simbol: altfel „E_c" s-ar prinde
      // în „E_cm", iar virgula dintre indici („E_c,ef") ar rupe simbolul
      if (estePartedinSimbol(text[i - 1])) continue;
      if (estePartedinSimbol(text[i + s.length])) continue;
      gasit = s;
      break;
    }
    if (gasit) {
      if (simplu) {
        out.push({ text: simplu });
        simplu = "";
      }
      out.push({ text: gasit, cheie: simboluri[gasit] });
      i += gasit.length;
    } else {
      simplu += text[i];
      i += 1;
    }
  }
  if (simplu) out.push({ text: simplu });
  return out;
}

/** Un text de la server, cu simbolurile lui devenite ancore de explicație.
 *
 *  Se pune peste formule, note, explicații și capete de tabel. Când catalogul
 *  nu a sosit încă, randează textul neatins — nimic nu se pierde, doar hoverul
 *  apare o clipă mai târziu. */
export default function TextSimboluri({
  text,
  valori,
  locale,
}: {
  text?: string | null;
  valori?: ValoriFormula;
  /** sensul simbolurilor ÎN ACEST context (de regulă `cat.simboluri` al unei
   *  verificări): bate explicația globală din registru, fiindcă același V_Ed
   *  e altceva la nod și la panoul de perete */
  locale?: SimbolLocal[];
}) {
  const catalog = useCatalogMarimi();
  if (!text) return null;
  const simboluri = catalog?.simboluri;
  if (!simboluri && !locale?.length) return <>{text}</>;
  const index: Record<string, string> = { ...(simboluri ?? {}) };
  const peLoc: Record<string, SimbolLocal> = {};
  for (const l of locale ?? []) {
    if (!l.simbol || l.simbol.length < 2 && /^[A-Za-z]$/.test(l.simbol)) continue;
    index[l.simbol] = `local:${l.simbol}`;
    peLoc[l.simbol] = l;
  }
  return (
    <>
      {bucatiCuSimboluri(text, index).map((b, k) =>
        b.cheie?.startsWith("local:") ? (
          <SLocal key={k} s={peLoc[b.text]}>
            {b.text}
          </SLocal>
        ) : b.cheie ? (
          <S key={k} k={b.cheie} valori={valori}>
            {b.text}
          </S>
        ) : (
          <Fragment key={k}>{b.text}</Fragment>
        )
      )}
    </>
  );
}

export interface SimbolLocal {
  simbol: string;
  nume: string;
  de_unde: string;
}

/** Ancora unui simbol explicat LOCAL, de verificarea pe cardul căreia stă. */
function SLocal({ s, children }: { s: SimbolLocal; children: ReactNode }) {
  const t = useT();
  return (
    <HoverCard
      clasaAncora="sb-ancora"
      eticheta={t("struct.info.explicatie_pentru", { ce: s.simbol })}
      continut={
        <CorpInfo
          titlu={`${s.simbol} — ${s.nume}`}
          ce={s.nume}
          deCe={
            <>
              <strong>{t("struct.simbol.de_unde_aici")}</strong> {s.de_unde}
            </>
          }
        />
      }
    >
      <span className="sb">{children}</span>
    </HoverCard>
  );
}


/** O frază de METODĂ din catalog: cu îngroșările ei ȘI cu simbolurile adnotate.
 *
 *  Paginile de calcul au fraze în care amândouă contează — «deplasarea se ia
 *  în <b>punctul cel mai defavorabil</b>, nu în d_re al centrului». `tx`
 *  singur ar lăsa simbolurile fără hover, `TextSimboluri` singur ar lăsa
 *  accentul plat; aici bucățile nemarcate trec prin adnotare, iar cele
 *  marcate rămân accent. */
export function TextMetoda({
  cheie,
  valori,
  locale,
}: {
  cheie: Cheie;
  valori?: Valori;
  locale?: SimbolLocal[];
}) {
  useT();
  return (
    <>
      {bucatiMarcate(t(cheie, valori)).map((b, i) => {
        const corp = <TextSimboluri text={b.text} locale={locale} />;
        if (b.marcaj === "b") return <strong key={i}>{corp}</strong>;
        if (b.marcaj === "i") return <em key={i}>{corp}</em>;
        if (b.marcaj === "code") return <code key={i}>{b.text}</code>;
        return <Fragment key={i}>{corp}</Fragment>;
      })}
    </>
  );
}
